import { Ellipsis, Pencil, Trash2 } from 'lucide-react';
import { ContactRow } from 'bindings/contacts/ContactRow';
import * as moment from 'moment';
import { ITag } from 'proto/protobufs';
import * as React from 'react';
import ContactsTemplateEn from 'templates/import_contacts_en.csv';
import ContactsTemplateFr from 'templates/import_contacts_fr.csv';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import { AContactEdition } from '../../atoms/contact/contactEdition';
import { AProfile, AUsers } from '../../atoms/global/users';
import ClientCompany from '../../components_v2/clientCompany/ClientCompany';
import { AddAndContactCreation } from '../../components_v2/creation/ContactCreation';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import AdvancedFilters from '../../components_v2/filter/AdvancedFilters';
import { FilterId, filterTreeLength } from '../../components_v2/filter/model/Model';
import { FilterParameter } from '../../components_v2/filter/pages/FilterList';
import InputSearch from '../../components_v2/input/InputSearch';
import Pagination from '../../components_v2/pagination/Pagination';
import { PaginationResult } from '../../components_v2/pagination/model/Model';
import PopupDeletion from '../../components_v2/popup/PopupDeletion';
import { Table, TableSortType } from '../../components_v2/table/Table';
import ToolbarFilterButton from '../../components_v2/toolbarFilter/ToolbarButton';
import { TagType } from '../../components_v2/toolbarFilter/tags/Model';
import { getTags, updateTags } from '../../components_v2/toolbarFilter/tags/action';
import { removeAllUrlParameters } from '../../components_v2/utils';
import { ToolBarView } from '../../components_v2/view/ToolBarView';
import { DefaultTextDiv } from '../../styles/global/css/GlobalText';
import { GreySidely3, RedSidely } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { useFunctionState, useRefState } from '../../utils/customHooks';
import { SelectAll, checkUrlModalParams } from '../client-companies/Companies';
import { DEFAULT_FILTER_RESULT } from '../client-companies/CompaniesAndMapView';
import { exportData } from '../client-companies/data/action';
import { CompanyStatus } from '../client-companies/model/Model';
import { SelectionAction, SelectionActionContainer } from '../client-companies/style/Style';
import { ToolbarBox, ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { ComponentProps } from '../globals/mainPage/mainPage';
import { getClientStatuses } from '../import/actions';
import { LoadingStateEnum } from '../import/model';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import NoData, { Unhautorized } from '../noData/NoData';
import { TypeFile } from '../orders/model/Model';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { ModalState } from '../products/model';
import { FlexDiv } from '../products/style';
import ContactColumns from './data/ContactColumns';
import { TableContext, TableProvider } from './data/ContactContext';
import { QueryUpdateContact, deleteContact, deleteContacts, getContactFilters, queryContact, updateContact } from './data/Data';
import ContactBulkEdition from './popup/ContactBulkEdition';
import ContactPopup from './popup/ContactPopup';
import { Translate } from 'react-localize-redux';
import { getLanguageCode } from '../reports/utils';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

const DEFAULT_STEP = 25;

const CONTACT_VIEW_LOCAL_STORAGE = 'contact_view_id';
export const CONTACT_HIDDEN_LOCAL_STORAGE = 'contact_hidden_columns';
export const CONTACT_SORT_LOCAL_STORAGE = 'contact_sort_columns';
export default function ContactsWrapper(props: ComponentProps) {
	return <TableProvider>
		<Contacts {...props} />
	</TableProvider>;
}

export type FilterState = {
	selectedId?: FilterId,
	openSummary?: boolean,
	filterOpen?: boolean
}


function Contacts(props: ComponentProps): JSX.Element {
	const [contactsRef, setContacts, contacts] = useRefState<ContactRow[]>([]);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [pagination, setPagination] = React.useState<PaginationResult>();
	const [sort, setSort] = React.useState<TableSortType | undefined>({ id: 'created_at', desc: true });
	const users = useRecoilValue(AUsers);
	const [selectedCheckboxRef, setSelectedCheckbox, selectedCheckbox] = useRefState<boolean[]>([]);
	const [isAllSelectedRef, setIsAllSelected, isAllSelected] = useRefState<boolean>(false, ({ newValue }) => {
		setSelectedCheckbox([]);
		return newValue;
	});
	const context = React.useContext(TableContext);
	const { hiddenColumns, sortColumns, filterResult, setFilterResult, setSortColumns, setHiddenColumns } = context;
	const tableRef = React.useRef<HTMLDivElement>();
	const [isBulkDeleteOpen, setIsBulkDeleteOpen] = React.useState<boolean>(false);
	const [isBulkEditOpen, setIsBulkEditOpen] = React.useState<boolean>(false);
	const [tableResize, setTableResize] = useFunctionState<{ [key: string]: number }>(
		JSON.parse(localStorage.getItem('table_contact_resize') || '{}'),
		({ newValue }) => {
			localStorage.setItem('table_contact_resize', JSON.stringify(newValue));
			return newValue;
		}
	);
	const [filterState, setFilterState] = React.useState<FilterState>({});
	const [search, setSearch] = React.useState<string>();
	const [filterParams, setFilterParams] = React.useState<FilterParameter[]>();
	const onClickFilter = React.useCallback((selectedId: FilterId) => {
		setFilterState({
			selectedId,
			openSummary: false,
			filterOpen: true
		});
	}, []);
	const [modalState, setModalState] = React.useState<ModalState<{ id: number, fullOpen: boolean }>>({ isOpen: false });
	const history = useHistory();
	const [companyState, setCompanyState] = React.useState<ModalState<number>>({ isOpen: false });
	const profile = useRecoilValue(AProfile);
	const refresh = () => setPagination(pagination => pagination ? ({ ...pagination }) : undefined);
	const [statuses, setStatuses] = React.useState<CompanyStatus[]>([]);
	const [tags, setTags] = React.useState<ITag[]>([]);
	const atomContactEdition = useRecoilValue(AContactEdition);
	const dispatch = useDispatch();
	const { isAllowedTo } = React.useContext(PermissionContext);

	React.useEffect(() => {
		if (!atomContactEdition) return;
		const contact = contacts.find(c => c.id === atomContactEdition.id);
		if (!contact) return;
		if (atomContactEdition.ids_delete) {
			contact.tag_ids = contact.tag_ids.filter(t => !atomContactEdition.ids_delete?.includes(t));
		}
		if (atomContactEdition.ids_add) {
			for (const id of atomContactEdition.ids_add) {
				contact.tag_ids.push(id);
			}
		}
		setContacts([...contacts]);
	}, [atomContactEdition]);

	React.useEffect(() => {
		const localHiddenColumns = JSON.parse(localStorage.getItem(CONTACT_HIDDEN_LOCAL_STORAGE) || '[]');
		const localSortColumns = JSON.parse(localStorage.getItem(CONTACT_SORT_LOCAL_STORAGE) || '[]');
		if (localSortColumns) setSortColumns(localSortColumns);
		if (localHiddenColumns) setHiddenColumns(localHiddenColumns);
	}, []);

	const updateTagsWrapper = React.useCallback(async(contactId: number, body: { ids_delete?: number[], ids_add?: number[] }) => {
		updateTags(TagType.CONTACT, {
			ressource_id: contactId,
			...body
		}).then(() => {
			const contact = contactsRef.current.find(c => c.id === contactId);
			if (!contact) return new Promise<void>((accept, _) => accept());
			body.ids_add?.forEach(t => contact.tag_ids.push(t));
			body.ids_delete?.forEach(t => contact.tag_ids = contact.tag_ids.filter(t2 => t2 !== t));
			setContacts([...contactsRef.current]);
			return new Promise<void>((accept, _) => accept());
		})
			.catch(e => new Promise<void>((_, refuse) => refuse(e)));
	}, []);

	const updateContactWrapper = React.useCallback(async(contactId: number, params: QueryUpdateContact, localChanges?: { [key: string]: unknown }) => {
		return updateContact(contactId, params)
			.then(_ => {
				const contact = contactsRef.current.find(c => c.id === contactId);
				if (!contact) return new Promise<void>((accept, _) => accept());
				contact.updated_at = moment().utc().toString();
				contact.updated_by = profile.id;
				const f = ([key, value]) => {
					if (typeof value === 'string' && value === 'deleted') return contact[key] = undefined;
					else if (typeof value === 'string' && value === 'unmodified') return;
					else if (typeof value === 'number' || typeof value === 'string') return contact[key] = value;
					else if ('updated' in value) return contact[key] = value.updated;
					else return contact[key] = value;
				};
				Object.entries(params).forEach(f);
				if (localChanges) Object.entries(localChanges).forEach(f);
				setContacts([...contactsRef.current]);
				return new Promise<void>((accept, _) => accept());
			})
			.catch(e => new Promise<void>((_, reject) => reject(e)));
	}, []);

	React.useEffect(() => {
		getContactFilters().then(setFilterParams);
		getClientStatuses().then(response => setStatuses(response.data));
		getTags(TagType.CONTACT).then(setTags);
	}, []);

	React.useEffect(() => {
		//TODO REDUCE CALLS
		setLoadingState(LoadingStateEnum.LOADING);
		queryContact({
			limit: pagination?.step ?? DEFAULT_STEP,
			offset: pagination?.offset ?? 0,
			filters: filterResult?.formatted,
			search,
			order_by: sort?.id,
			descending: sort?.desc,
		}).then(res => {
			setContacts(res);
			setLoadingState(LoadingStateEnum.LOADED);
		});
	}, [pagination, sort, filterResult, search]);
	const LocalExportContacts = () => {
		dispatch({ type: 'INSERT_MESSAGE', value: { message: { text: 'exports.ready_toast' }, state: 'loading' } });
		exportData(TypeFile.Excel, {
			type: 'Contacts',
			filters: filterResult?.formatted,
			order_by: sort?.id,
			descending: sort?.desc,
			search
		});
	};

	React.useEffect(() => {
		props.setToolBarState({
			title: 'Contact',
			bottomLeftToolbarComponent: <ToolbarBox>
				<ToolBarView
					localStorageKey={CONTACT_VIEW_LOCAL_STORAGE}
					localStorageHiddenKey={CONTACT_HIDDEN_LOCAL_STORAGE}
					viewsKey='contacts'
					context={context}
				>
					<ToolbarFilterButton
						activeFilters={filterTreeLength(filterResult?.formatted)}
						onClick={() => setFilterState(state => ({ ...state, openSummary: true, filterOpen: true }))}
						onDeleteFilter={() => {
							setFilterResult({ values: { combinator: 'and', array: [] }, formatted: undefined });
						}}
					/>
					{(selectedCheckbox.some(c => c) || isAllSelected) && <FlexDiv gap='10px'>
						<DefaultTextDiv height='30px'>
							<SelectionActionContainer>
								<Restricted to={{ objectAction: 'UpdateContact' }}>
									<SelectionAction onClick={() => setIsBulkEditOpen(true)} titleHover={translateToString('edit_selection')}>
										<Pencil />
									</SelectionAction>
								</Restricted>
								<Restricted to={{ objectAction: 'DeleteContact' }}>
									<SelectionAction onClick={() => setIsBulkDeleteOpen(true)} delete titleHover={translateToString('delete_selection')}>
										<Trash2 color={RedSidely} />
									</SelectionAction>
								</Restricted>
							</SelectionActionContainer>
						</DefaultTextDiv>
					</FlexDiv>}
				</ToolBarView>
			</ToolbarBox>,
			bottomRightToolbarComponent: <ToolbarBox>
				<InputSearch
					placeholder={translateToString('search')}
					name='search_contact_name'
					type='text'
					value={search}
					onChange={setSearch}
					delay={500}
				/>
				<Restricted to={{ objectAction: 'CreateContact' }}>
					<AddAndContactCreation
						onCreate={c => {
							refresh();
							history.push({
								search: `?id=${c.contact_id}`
							});
							setModalState(state => state.data ? { ...state, data: { ...state.data, fullOpen: true } } : { ...state, data: { id: c.contact_id, fullOpen: true } });
						}}
					/>
				</Restricted>
				<Restricted to={{ objectAction: 'CreateExport' }}>
					<Dropdown
						dropdownStyle={{ optionLeft: '-115px', optionWidth: '160px' }}
						datalist={[{ label: translateToString('export_NAME', [['NAME', translateToString('contacts').toLocaleLowerCase()]]), value: 'EXPORT' }, ...(isAllowedTo({ objectAction: 'CreateImport' }) ? [{ label: translateToString('general.import'), value: 'IMPORT' }] : [])]}
						name='options'
						JSXButton={() => <ToolbarImageContainer hasPointer ><Ellipsis size={18} strokeWidth={2.5} color={GreySidely3} /></ToolbarImageContainer>}
						onChange={value => {
							switch (value.value) {
								case 'EXPORT': {
									LocalExportContacts();
									return;
								}
								case 'IMPORT': {
									history.push({ pathname: '/settings', hash: 'import' });
									return;
								}
							}
						}}
						searchable={false}
					/>
				</Restricted>
			</ToolbarBox>
		});
	}, [filterResult, selectedCheckbox, isAllSelected, hiddenColumns, sortColumns, search]);

	React.useEffect(() => {
		const f = ({ isOpen, isFullOpen, id }) => {
			const parsedId = parseInt(id);
			if (isNaN(parsedId)) return;
			setModalState({ isOpen, data: { fullOpen: isFullOpen, id: parsedId } });
		};
		checkUrlModalParams(f)();
		history.listen(checkUrlModalParams(f));
	}, [history]);

	const columns = React.useMemo(() => ContactColumns(
		users,
		(value, fullOpen) => {
			history.push({
				search: `?id=${value}`
			});
			setModalState((state) => state.data ? { ...state, data: { ...state.data, fullOpen } } : { ...state, data: { id: -1, fullOpen } });
		},
		data => setCompanyState({ isOpen: true, data }),
		updateContactWrapper,
		id => deleteContact(id).then(refresh),
		// row => {console.log(row);}
		loadingState,
		statuses,
		tags,
		updateTagsWrapper
	), [users, loadingState, statuses, tags, hiddenColumns, sortColumns]);

	const onSort = React.useCallback((sort: TableSortType) => setSort(sort[0]), []);

	const hasSearchOrFilters = (search?.trim().length ?? 0) > 0 || filterTreeLength(filterResult?.formatted) > 0;

	return <Restricted to={{ objectAction: 'ReadContact' }} fallback={<Unhautorized />}>
		{contacts.length > 0 ?
			<Table
				allSelectable
				innerRef={tableRef}
				height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
				columns={columns}
				data={contacts}
				onSort={onSort}
				EnableResize
				initialSortBy={sort}
				setSelectedCheckbox={row => {
					if (row) {
						selectedCheckboxRef.current[row.id] = !selectedCheckboxRef.current[row.id];
						setSelectedCheckbox([...selectedCheckboxRef.current]);
						return selectedCheckboxRef.current[row.id];
					} else {
						const actualState = contactsRef.current.every(c => !!selectedCheckboxRef.current[c.id] === !isAllSelectedRef.current);
						const newSelectedCheckbox: boolean[] = [...selectedCheckboxRef.current];
						contactsRef.current.forEach(c => newSelectedCheckbox[c.id] = isAllSelectedRef.current != !actualState);
						setSelectedCheckbox(newSelectedCheckbox);
						return !actualState;
					}
				}}
				isIdSelected={row => {
					if (row) return isAllSelectedRef.current != (selectedCheckboxRef.current[row.id] ?? false);
					if (isAllSelectedRef.current) return selectedCheckboxRef.current.every((b, id) => !b || !contactsRef.current.some(c => c.id === id));
					return contactsRef.current.length > 0 && selectedCheckboxRef.current.filter(b => b).length === contactsRef.current.length;
				}}
				hiddenColumns={hiddenColumns}
				sortColumns={sortColumns}
				onClickFilter={onClickFilter}
				onResize={(value) => Object.keys(value).length > 0 && setTableResize(value)}
				resizeValue={tableResize}
				isInformationHeaderOpen={(selectedCheckbox.some(c => c) && selectedCheckbox.length !== 0) || isAllSelected}
				informationHeader={<SelectAll
					onMaxSelected={setIsAllSelected}
					selected={selectedCheckbox.filter(c => c).length}
					max={Number(contacts[0]?.count ?? 0)}
					isAllSelected={isAllSelected}
				/>}
			/>
			: hasSearchOrFilters
				? <NoData
					isNoResult
					message={translateToString('No results found')}
					messageButton={<Translate id='reset_filters' />}
					onClickButton={() => {
						setFilterResult(DEFAULT_FILTER_RESULT);
						setSearch(undefined);
					}}
				/>
				: <NoData
					message={translateToString('no_contact')}
					messageButton={<Translate id='general.import' />}
					messageButton2={<Translate id='export_model' />}
					onClickButton={() => history.push({ pathname: '/settings', hash: 'import' })}
					onClickButton2={() => {
						const a = document.createElement('a');
						a.href = getLanguageCode() === 'fr' ? ContactsTemplateFr : ContactsTemplateEn;
						a.download = 'contacts_template.csv';
						a.click();
					}} />
		}
		<ComponentLoader loadingState={loadingState} allScreen />
		<Pagination
			label={translateToString('contacts')}
			amount={Number(contacts[0]?.count ?? 0)}
			currentCount={contacts.length}
			steps={[DEFAULT_STEP, 50, 100]}
			onChange={setPagination}
		/>
		<ContactPopup
			isOpen={modalState.isOpen}
			setOpen={(isOpen: boolean) => {
				if (!isOpen) { removeAllUrlParameters(); }
				setModalState({ isOpen });
			}}
			contactId={modalState.data?.id}
			fullOpenMode={modalState.data?.fullOpen}
			updateContact={updateContactWrapper}
			tags={tags}
		/>
		<AdvancedFilters
			permission='ReadContact'
			isOpen={filterState.filterOpen ?? false}
			setOpen={filterOpen => setFilterState({ filterOpen })}
			isOpenSummary={filterState.openSummary ?? false}
			filterList={filterParams}
			filterValues={filterResult?.values}
			onChange={(value) => setFilterResult(value)}
			selectedFilter={filterState.selectedId}
			tagType={TagType.CONTACT}
		/>
		<ClientCompany
			isOpen={companyState.isOpen}
			setOpen={(isOpen: boolean) => setCompanyState({ isOpen })}
			clientCompanyId={companyState.data!}
		/>
		<PopupDeletion
			isOpen={isBulkDeleteOpen}
			onClickOut={() => setIsBulkDeleteOpen(false)}
			records={isAllSelected ? Number(contacts[0]?.count ?? 0) - selectedCheckbox.filter(c => c).length : selectedCheckbox.filter(c => c).length}
			onValidation={() => {
				deleteContacts(selectedCheckbox.reduce((acc, elem, i) => elem ? [...acc, i] : acc, []), isAllSelected, filterResult?.formatted, undefined)
					.then(refresh);
				setSelectedCheckbox([]);
				setIsAllSelected(false);
			}}
			translationKey='contacts'
		/>
		<ContactBulkEdition
			isOpen={isBulkEditOpen}
			setIsOpen={setIsBulkEditOpen}
			ids={selectedCheckbox.reduce((acc, elem, i) => elem ? [...acc, i] : acc, [])}
			refresh={() => {
				setIsAllSelected(false);
				refresh();
				setIsBulkEditOpen(false);
			}}
			isAll={isAllSelected}
			filters={filterResult?.formatted}
			statuses={statuses}
		/>
	</Restricted>;
}
